import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
} from "@mui/material";
import { subHeading } from "../../Components/CustomTexts";
import { rowHorizontalEnd, rowSB } from "../../GlobalStyles";
import CustomIconButton from "../../Components/CustomIconButton";
import SvgPreview from "../../Components/SvgPreview";
import { cross } from "../../assets/svgs";
import CustomContainedButton from "../../Components/CustomContainedButton";
import CustomTextField from "../../Components/CustomTextfield";
import { StopScroll } from "../../store/slices/constants/staticContents";

interface ClusterConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: {
    separate_cluster: boolean;
    cluster_type: string;
    min_time: number;
    max_time: number;
  }) => void;
}

const ClusterConfigDialog: React.FC<ClusterConfigDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [form, setForm] = useState({
    separate_cluster: false,
    cluster_type: "informative",
    min_time: 30,
    max_time: 60,
  });

  const handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onConfirm(form);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 0.5 },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{ m: 0, p: "20px 15px 15px", ...rowSB }}
        id="customized-dialog-title"
      >
        {subHeading({ text: "Cluster Settings", size: "1rem", weight: "500" })}
        <CustomIconButton
          onClick={onClose}
          noHover={true}
          content={<SvgPreview svg={cross} />}
        />
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers sx={{ px: 3, pt: 2, pb: 1 }}>
        <Box display="flex" flexDirection="column" gap={3} mt={1}>
          {/* Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={form.separate_cluster}
                onChange={(e) =>
                  handleChange("separate_cluster", e.target.checked)
                }
              />
            }
            label="Separate each question into its own cluster"
          />

          {/* Conditional Settings */}
          {form.separate_cluster && (
            <>
              {/* Cluster Type */}
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                  Select Cluster Type:
                </FormLabel>
                <RadioGroup
                  value={form.cluster_type}
                  onChange={(e) => handleChange("cluster_type", e.target.value)}
                  sx={{ display: "flex", flexDirection: "column", mt: 1 }}
                >
                  <FormControlLabel
                    value="cognitive"
                    control={<Radio />}
                    label="Cognitive"
                  />
                  <FormControlLabel
                    value="informative"
                    control={<Radio />}
                    label="Informative"
                  />
                  <FormControlLabel
                    value="vocab"
                    control={<Radio />}
                    label="Vocabulary"
                  />
                </RadioGroup>
              </FormControl>

              {/* Time Fields */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "100%",
                  mt: 1,
                }}
              >
                {/* Max Time */}
                <Box sx={{ width: "calc(50% - 5px)", mr: "10px" }}>
                  {subHeading({ text: "Maximum Time (sec) *", weight: 600 })}
                  <CustomTextField
                    placeholder="Maximum time"
                    type="number"
                    value={form.max_time}
                    required={true}
                    onFocus={StopScroll}
                    onKeyDown={(evt) => {
                      if (["e", "E", "-", "+"].includes(evt.key)) {
                        evt.preventDefault();
                      }
                      if ([38, 40].includes(evt.keyCode)) {
                        evt.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value !== 0) {
                        const newV =
                          value < form.min_time ? form.min_time : value;
                        handleChange("max_time", newV);
                      } else {
                        handleChange("max_time", "");
                      }
                    }}
                    sx={{
                      width: "100%",
                      mt: "5px",
                      "& .MuiInputBase-root": { padding: "0" },
                      "& .MuiInputBase-input": { padding: "10px 12px" },
                    }}
                  />
                </Box>

                {/* Min Time */}
                <Box sx={{ width: "calc(50% - 5px)" }}>
                  {subHeading({ text: "Minimum Time (sec) *", weight: 600 })}
                  <CustomTextField
                    placeholder="Minimum time"
                    type="number"
                    value={form.min_time}
                    required={true}
                    onFocus={StopScroll}
                    onKeyDown={(evt) => {
                      if (["e", "E", "-", "+"].includes(evt.key)) {
                        evt.preventDefault();
                      }
                      if ([38, 40].includes(evt.keyCode)) {
                        evt.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value !== 0) {
                        const newV =
                          value > form.max_time ? form.max_time : value;
                        handleChange("min_time", newV);
                      } else {
                        handleChange("min_time", "");
                      }
                    }}
                    sx={{
                      width: "100%",
                      mt: "5px",
                      "& .MuiInputBase-root": { padding: "0" },
                      "& .MuiInputBase-input": { padding: "10px 12px" },
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ m: 0, p: "10px", ...rowHorizontalEnd }}>
        <CustomIconButton onClick={onClose} content="Cancel" />
        <CustomContainedButton
          content="Submit"
          otherProps={{
            onClick: handleSubmit,
            disabled: form.separate_cluster
              ? form.min_time && form.max_time && form.cluster_type
                ? false
                : true
              : false,
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ClusterConfigDialog;
